import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AppDataService, OperatorService } from 'app/services';
import { AppComponent } from 'app/app.component';
import { MessageService } from 'primeng/api';
import { DriverAwardType } from 'app/models';
import moment from 'moment';
import { Utils } from 'app/utils';
import { PerformanceRate, RateType } from 'app/home';
import { StylesPerfCard } from 'app/models/users/StylesPerfCard';
import { DashboardDatePreset, DashboardDateSelection } from 'app/components/operator/dashboard/operator-dashboard-date-picker.component';

@Component({
    selector: 'operator-vehicle-insights',
    templateUrl: './operator-vehicle-insights.component.html',
    styleUrls: ['./operator-vehicle-insights.component.scss',
        '../../../../../../styles/menu-sidebar.scss',
        '../../../../../../styles/style-layout.scss'
    ]
})
export class OperatorVehicleInsightsComponent implements OnInit {
    @Input()
    public vehicleId!: number;

    constructor(private messageService: MessageService, public appData: AppDataService,
        public app: AppComponent, private operatorService: OperatorService) { }

    public isLoading: boolean = true;

    public vehicleStatistics!: any;
    public activeHoursChartData: any;
    public activeHoursChartOptions: any;
    public selectedDateRange: Date[] = [new Date(), new Date()];
    public vehicleDatePreset: DashboardDatePreset = 'today';
    public ratingPercentages: any[] = [];
    public customerFeedbackPercents: any[] = [];
    public driverAwardTypeEnum = DriverAwardType;

    ngOnInit(): void {
        this.getVehicleStatistics();
    }

    getPerfRate(rateType: RateType, rateValue: number): PerformanceRate {
        switch (rateType) {
            case RateType.Completion:
            case RateType.Acceptance:
                if (rateValue > 70) {
                    return PerformanceRate.Good;
                } else if (rateValue <= 70 && rateValue > 50) {
                    return PerformanceRate.Medium;
                } else {
                    return PerformanceRate.Bad;
                }
            case RateType.Cancellation:
                if (rateValue < 30) {
                    return PerformanceRate.Good;
                } else if (rateValue >= 30 && rateValue <= 50) {
                    return PerformanceRate.Medium;
                } else {
                    return PerformanceRate.Bad;
                }
            default: return PerformanceRate.None;
        }
    }

    public getPerfCardColors(rateType: RateType, rateValue: number): StylesPerfCard {
        let backgroundColor: string;
        let borderColor: string;
        switch (rateType) {
            case RateType.Completion:
            case RateType.Acceptance:
                if (rateValue > 70) {
                    backgroundColor = '#CEE6FF';
                    borderColor = '#0A84FF';
                } else if (rateValue <= 70 && rateValue > 50) {
                    backgroundColor = '#FDE5CC';
                    borderColor = '#F77F00';
                } else {
                    backgroundColor = '#FEDED8';
                    borderColor = '#EB4236';
                }
                break;
            case RateType.Cancellation:
                if (rateValue < 30) {
                    backgroundColor = '#CEE6FF';
                    borderColor = '#0A84FF';
                } else if (rateValue >= 30 && rateValue <= 50) {
                    backgroundColor = '#FDE5CC';
                    borderColor = '#F77F00';
                } else {
                    backgroundColor = '#FEDED8';
                    borderColor = '#EB4236';
                }
                break;
            default:
                backgroundColor = '';
                borderColor = '';
        }

        return {
            backgroundColor,
            borderColor
        };
    }

    public getVehicleStatistics() {
        const [startDate, endDate] = this.selectedDateRange || [];

        this.isLoading = true;

        const dto = {
            VehicleId: this.vehicleId,
            StartDate: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
            EndDate: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
            TimeZoneOffset: Utils.getTimezoneOffset()
        };
        this.operatorService.getOperatorVehicleStatistics(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    this.isLoading = false;
                    return;
                }
                this.vehicleStatistics = data;
                this.vehicleStatistics.RiderRatingRounded = Math.floor(data.RiderRating);
                this.vehicleStatistics.CustomerNotes = this.vehicleStatistics.CustomerNotes.map((x: any) => {
                    x.CreatedAt = moment(Utils.utcToLocaleDateString(x.CreatedAt)).format('DD MMM YYYY');
                    return x;
                });
                this.vehicleStatistics.HoursOnline = Math.round(this.vehicleStatistics.HoursOnline * 10) / 10;

                for (let i = 0; i <= 4; i++) {
                    this.ratingPercentages[i + 1] = data.CountMarks !== 0 ? Math.round((this.vehicleStatistics.Marks[i].Count / data.CountMarks) * 100) : 0;
                }
                for (let i = 0; i < data.CustomerFeedback.length; i++) {
                    this.customerFeedbackPercents[this.vehicleStatistics.CustomerFeedback[i].DriverAwardId] = data.CompletedJobsPerPeriod !== 0 ?
                        Math.round((this.vehicleStatistics.CustomerFeedback[i].Count / data.CompletedJobsPerPeriod) * 100) : 0;
                }

                this.activeHoursChartInit();
                this.isLoading = false;
            })
            .catch(err => {
                this.isLoading = false;
                throw err;
            });
    }

    public activeHoursChartInit(): void {
        this.activeHoursChartData = {
            labels: this.vehicleStatistics.ActiveHoursLabels,
            datasets: [
                {
                    type: 'bar',
                    backgroundColor: '#004060',
                    data: this.vehicleStatistics.ActiveHoursStat,
                    borderColor: 'white',
                    borderWidth: 2
                }
            ]
        };

        this.activeHoursChartOptions = {
            plugins: {
                legend: {
                    display: false
                }
            }
        };
    }

    get vehicleDateStart(): string {
        return moment(this.selectedDateRange?.[0] || new Date()).format('YYYY-MM-DD');
    }

    get vehicleDateEnd(): string {
        return moment(this.selectedDateRange?.[1] || new Date()).format('YYYY-MM-DD');
    }

    onVehicleDateApply(selection: DashboardDateSelection): void {
        this.vehicleDatePreset = selection.preset;
        this.updateVehicleStats(selection);
    }

    updateVehicleStats(datesFilter: any) {
        this.selectedDateRange[0] = datesFilter.startDate;
        this.selectedDateRange[1] = datesFilter.endDate;
        this.getVehicleStatistics();
    }

    public getReviewStarSrc(userMark: number): string | null {
        if (userMark === 1 || userMark === 2) {
            return '../Content/xpress-app/images/star-red.svg';
        }
        if (userMark === 3 || userMark === 4) {
            return '../Content/xpress-app/images/star-yellow.svg';
        }
        if (userMark === 5) {
            return '../Content/xpress-app/images/star-green.svg';
        }
        return null;
    }

    public getMaskedReviewNote(note: string | null | undefined): string {
        if (!note) {
            return '';
        }
        // Operator portal must not expose free-text review content that may include customer PII.
        return '••••••••';
    }

    protected readonly RateType = RateType;
}
